import { BracketMatchCard } from '@/components/knockout/BracketMatchCard'
import { Icon, ICONS } from '@/lib/icons'
import type { KnockoutBracket, KnockoutMatch, Player } from '@/types/api'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getRoundLabel } from './utils'

// Helper to check if bracket can be completed
function canCompleteBracket(bracket: KnockoutBracket): boolean {
  // Find the final match (round 1)
  const finalMatch = bracket.matches.find(m => m.roundNumber === 1 && m.positionInRound === 0)
  if (!finalMatch?.completed) return false

  // If bronze medal match exists, it must also be completed
  if (bracket.hasBronzeMedalMatch && bracket.bronzeMedalMatchId) {
    const bronzeMatch = bracket.matches.find(m => m.id === bracket.bronzeMedalMatchId)
    if (bronzeMatch && !bronzeMatch.completed) return false
  }

  return true
}

interface BracketViewProps {
  bracket: KnockoutBracket
  playersById: Record<string, Player>
  onReportMatch?: (match: KnockoutMatch) => void
  onEditMatch?: (match: KnockoutMatch) => void
  onCompleteBracket?: () => void
}

export function BracketView({ bracket, playersById, onReportMatch, onEditMatch, onCompleteBracket }: BracketViewProps) {
  const { t } = useTranslation()

  const matchesByRound = useMemo(() => {
    const grouped = new Map<number, KnockoutMatch[]>()
    for (const match of bracket.matches) {
      const list = grouped.get(match.roundNumber) ?? []
      list.push(match)
      grouped.set(match.roundNumber, list)
    }

    for (const [round, list] of grouped.entries()) {
      list.sort((a, b) => a.positionInRound - b.positionInRound)
      grouped.set(round, list)
    }

    return grouped
  }, [bracket.matches])

  const matchByKey = useMemo(() => {
    const map = new Map<string, KnockoutMatch>()
    for (const match of bracket.matches) {
      map.set(`${match.roundNumber}-${match.positionInRound}`, match)
    }
    return map
  }, [bracket.matches])

  const resolveSourceLabel = useCallback((sourceKey?: string, isBronzeMatch = false) => {
    if (!sourceKey) return undefined
    const [roundPart, positionPart] = sourceKey.split('-')
    const roundNumber = Number.parseInt(roundPart ?? '', 10)
    const positionInRound = Number.parseInt(positionPart ?? '', 10)

    if (Number.isNaN(roundNumber) || Number.isNaN(positionInRound)) {
      return undefined
    }

    const sourceMatch = matchByKey.get(`${roundNumber}-${positionInRound}`)
    if (!sourceMatch) return undefined

    const roundLabel = getRoundLabel(roundNumber, bracket.totalRounds, t)
    
    // For bronze match, show "Loser of Semifinal X" instead of "Winner"
    // Semifinals have roundNumber === totalRounds (e.g., round 2 for 4-player bracket)
    if (isBronzeMatch && roundNumber === bracket.totalRounds) {
      return t('knockout.round.matchLoser', {
        round: roundLabel,
        match: sourceMatch.positionInRound + 1
      })
    }
    
    return t('knockout.round.matchWinner', {
      round: roundLabel,
      match: sourceMatch.positionInRound + 1
    })
  }, [matchByKey, bracket.totalRounds, t])

  const orderedRounds = useMemo(() => {
    const highestRound = bracket.totalRounds
    const rounds = Array.from({ length: highestRound }, (_, index) => highestRound - index)
    
    // Include bronze medal match (round 0) if it exists
    const hasBronzeMatch = bracket.matches.some(m => m.roundNumber === 0)
    if (hasBronzeMatch) {
      rounds.push(0)
    }
    
    return rounds
  }, [bracket.totalRounds, bracket.matches])

  // Simple vertical card-based layout
  return (
    <div className="space-y-4">

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon icon={ICONS.tournament} width={18} height={18} />
        <span>{t('knockout.bracketSummary', { size: bracket.bracketSize })}</span>
      </div>

      <div className="space-y-6">
        {orderedRounds.map((roundNumber) => {
          const matches = matchesByRound.get(roundNumber) ?? []
          const roundLabel = getRoundLabel(roundNumber, bracket.totalRounds, t)
          
          return (
            <div key={roundNumber} className="space-y-3">
              <div className="flex items-center gap-2 border-b pb-2">
                <div className="text-base font-semibold text-foreground">{roundLabel}</div>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {matches.length === 0 ? (
                  <EmptyRound />
                ) : (
                  matches.map((match) => (
                    <BracketMatchCard
                      key={match.id}
                      match={match}
                      playersById={playersById}
                      resolveSourceLabel={resolveSourceLabel}
                      onReportMatch={onReportMatch}
                      onEditMatch={onEditMatch}
                    />
                  ))
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Complete Bracket Button */}
      {onCompleteBracket && bracket.state === 'KNOCKOUT_BRACKET_STATE_ACTIVE' && canCompleteBracket(bracket) && (
        <div className="mt-6 p-4 bg-success-muted border border-success/30 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon icon={ICONS.trophy} width={20} height={20} className="text-success" />
              <span className="text-sm font-medium text-success-foreground">
                {t('tournament.complete.bracket.warning')}
              </span>
            </div>
            <button
              onClick={onCompleteBracket}
              className="px-4 py-2 bg-success text-success-foreground rounded-md hover:bg-success/90 transition-colors"
            >
              {t('tournament.complete.bracket.button')}
            </button>
          </div>
        </div>
      )}

      {bracket.state === 'KNOCKOUT_BRACKET_STATE_COMPLETED' && (
        <div className="mt-6 p-4 bg-info-muted border border-info/30 rounded-lg text-info-muted-foreground">
          <div className="flex items-center gap-2">
            <Icon icon={ICONS.checkCircle} width={20} height={20} className="text-info" />
            <span className="text-sm font-medium">
              {t('tournament.complete.bracket.completed')}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

function EmptyRound() {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center text-sm text-muted-foreground">
      {t('knockout.round.empty', 'No matches scheduled for this round yet')}
    </div>
  )
}
